import type { Category, DocumentType } from './api'
import { CATEGORY_TYPE } from './labels'

/**
 * O ponto de partida de um documento novo: a estrutura do tipo, com os blocos obrigatórios do
 * dialeto já no lugar (docs/styleguide/08-legal-design.md). É texto de orientação para quem
 * escreve, não um documento pronto: tudo aqui é substituído no editor.
 */

function frontMatter(title: string, type: DocumentType, procedure: string | null) {
  const lines = ['---', `title: ${JSON.stringify(title)}`, `type: ${type}`]
  if (procedure) lines.push(`procedure: ${JSON.stringify(procedure)}`)
  lines.push('---', '')
  return lines.join('\n')
}

const CONSENT_FORM = `:::summary
Diga em uma ou duas frases, em linguagem clara, o que vai acontecer e quem faz.
:::

:::authorization
O procedimento, a região e o que foi combinado na avaliação.
:::

:::risks
Os efeitos esperados e os riscos, inclusive o que não caracteriza erro.
:::

:::parties

- **Paciente**: {{patient.name}}, {{patient.idDocument}}
- **Clínica**: {{clinic.legalName}}, {{clinic.idDocument}}
- **Profissional responsável**: {{practitioner.name}}, {{practitioner.fullRegistration}}

:::

## O procedimento

### O que será feito

Descreva o procedimento.

## O que você declara

### Informações de saúde

Você declara que as respostas dadas na anamnese são verdadeiras.

## Cuidados e retorno

1. Primeiro cuidado.

:::signatures

- Paciente: {{patient.name}}
- Profissional responsável: {{practitioner.name}}

:::
`

const CONTRACT = `:::summary
Diga em uma ou duas frases o que este contrato combina entre as partes.
:::

:::parties

- **Contratante**: {{patient.name}}, {{patient.idDocument}}
- **Contratada**: {{clinic.legalName}}, {{clinic.idDocument}}, {{clinic.fullAddress}}

:::

## Objeto

### O que está sendo contratado

Descreva o objeto do contrato.

## Valor e pagamento

### Valor

O valor total é de {{appointment.totalAmount}}.

### Condição de pagamento

{{appointment.paymentTerms}}

## Disposições gerais

### Foro

Indique o foro.

{{signature.placeAndDate}}

:::signatures

- Contratante: {{patient.name}}
- Contratada: {{clinic.legalName}}

:::
`

const STANDALONE = `:::summary
Diga em uma ou duas frases o que este termo registra.
:::

:::parties

- **Declarante**: {{patient.name}}, {{patient.idDocument}}
- **Clínica**: {{clinic.legalName}}, {{clinic.idDocument}}

:::

## Declaração

### O que fica registrado

Escreva o que a pessoa declara.

{{signature.placeAndDate}}

:::signatures

- Declarante: {{patient.name}}

:::
`

const BLANK = `## Primeira seção

Escreva o documento. Comece pelo resumo em linguagem clara.
`

const BODY: Record<DocumentType, string> = {
  consent_form: CONSENT_FORM,
  contract: CONTRACT,
  standalone: STANDALONE,
  anamnesis: STANDALONE,
  other: BLANK,
}

/** O Markdown inicial com a estrutura do tipo, ou só o front matter quando `blank`. */
export function starterMarkdown(options: {
  name: string
  category: Category
  procedure: string | null
  blank?: boolean
}) {
  const type = CATEGORY_TYPE[options.category]
  const head = frontMatter(options.name, type, options.procedure)
  return head + (options.blank ? BLANK : BODY[type])
}

/**
 * A cópia de outro documento, com o título, o procedimento e o tipo do novo. O tipo é o da
 * categoria escolhida, não o da origem: a API só publica quando o `type` do cabeçalho é o do
 * documento, e o tipo de um documento não se edita depois de criado.
 */
export function copyMarkdown(
  source: string,
  options: { name: string; category: Category; procedure: string | null },
) {
  const body = source.replace(/^---\r?\n[\s\S]*?\r?\n---[ \t]*(?:\r?\n)*/, '')
  return frontMatter(options.name, CATEGORY_TYPE[options.category], options.procedure) + body
}
